import React, { createContext, useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Editable, withReact, Slate } from 'slate-react';
import { withHistory } from 'slate-history';
import { createEditor } from 'slate';
import classnames from 'classnames';
import isHotkey from 'is-hotkey';
import { useUpdateEffect, useDebounce } from 'react-haiku';

// Components
import Toolbar from 'components/Editor/components/Toolbar';

// Utils
import withUtils from 'components/Editor/utils/Utils';
import withHeadings from 'components/Editor/utils/Headings';
import withMarks from 'components/Editor/utils/Marks';
import withHighlights from 'components/Editor/utils/Highlights';
import withAlignments from 'components/Editor/utils/Alignments';
import withBlockquote from 'components/Editor/utils/Blockquote';
import withFormulas from 'components/Editor/utils/Formulas';
import withLinks from 'components/Editor/utils/Links';
import withLists from 'components/Editor/utils/Lists';
import withTables from 'components/Editor/utils/Tables';
import withMedia from 'components/Editor/utils/Media';
import withSpellcheck from 'components/Editor/utils/Spellcheck';
import withQuiz from 'components/Editor/utils/Quiz';
import withDisabled from 'components/Editor/utils/Disabled';
import withNormalization from 'components/Editor/utils/Normalization';

// Renderers
import Element from 'components/Editor/render/Element';
import Leaf from 'components/Editor/render/Leaf';
import Show from 'components/Show';

export const EditorContext = createContext();

// Rich text editor's component
// @type: string | editor's type
// @label: string | editor's label
// @name: string | editor's name
// @value: string | parent's initial value
// @words: array | spellcheck's word array
// @onChange: function | parent's onchange handler
const Editor = ({
	type,
	label,
	name,
	value, 
	error,
	path,
	folder,
	readOnly,
	onChange,
	spellcheck,
	quiz
}) => {
	// Editor's reference
	const editor = useRef();

	// Use ref for parsed flag
	const parsed = useRef(false);

	const [quizContext, setQuizContext] = useState({
		mode: quiz?.mode
	});

	const onUpdateQuizMode = mode => {
		setQuizContext(prev => ({
			...prev,
			mode
		}));
	};

	// Editor's value
	const [instance, setInstance] = useState([
		{
			type: 'paragraph',
			children: [{ text: '' }]
		}
	]);

	// Debounced editor's value
	const debounced = useDebounce(instance, 150);

	// Cache editor's ref and initialize all plugins
	if (!editor.current) editor.current = withUtils(
		withMarks(
			withHeadings(
				withHighlights(
					withAlignments(
						withBlockquote(
							withFormulas(
								withLinks(
									withTables(
										withLists(
											withMedia(
												withSpellcheck(
													withQuiz(
														withDisabled(
															withNormalization(
																withHistory(
																	withReact(
																		createEditor()
																	)
																)
															)
														)
													)
												)
											)
										)
									)
								)
							)
						)
					)
				)
			)
		)
	);

	// Render elements
	const renderElement = useCallback(props => <Element {...props} />, []);

	// Render leafs
	const renderLeaf = useCallback(props => <Leaf {...props} />, []);
	
	// Handles onKeyDown event in editor
	// event: object | default onKeyDown event
	const handleKeyDown = event => {
		// Tab hotkeys
		if (isHotkey('tab', event) && editor.current.isInTable()) {
			event.preventDefault();
			return editor.current.moveTableSelectionForwards();
		}
		
		// Shift+tab hotkeys
		if (isHotkey('shift+tab', event) && editor.current.isInTable()) {
			event.preventDefault();
			return editor.current.moveTableSelectionBackwards();
		}

		// Mark hotkeys
		if (isHotkey('mod+b', event)) editor.current.toggleMark('bold');
		if (isHotkey('mod+i', event)) editor.current.toggleMark('italic');
		if (isHotkey('mod+u', event)) editor.current.toggleMark('underline');
		if (isHotkey('mod+k', event)) editor.current.toggleFormula();
	};

	const handlePaste = event => {
		console.log('paste');
	};

	// Handles editor's value change
	// @value: object | editor's value
	const handleChange = useCallback(value => {
		// Update instance's value
		setInstance(value);
	}, []);

	// Update parent's component value with debounced value
	useUpdateEffect(() => {
		if (onChange) onChange({
			name,
			value: JSON.stringify(debounced)
		});
	}, [debounced]);

	useEffect(() => {
		// Do not reinitialize value
		if (parsed.current) return;

		// Do not try to initialize an invalid value
		if (typeof value !== 'string') return;

		// Do not try to initialize an empty value
		if (value === '') return;

		// Update parsed flag
		parsed.current = true;

		// Set instance value to given value
		setInstance(JSON.parse(value));
	}, [value]);

	useEffect(() => {
		// Do not reinitialize if not in readOnly mode
		if (!readOnly) return;

		// Do not try to initialize an invalid value
		if (typeof value !== 'string') return;

		// Do not try to initialize an empty value
		if (value === '') return;

		// Set instance value to given value
		setInstance(JSON.parse(value));
	}, [readOnly, value]);

	// Editor's classnames
	const editorClassname = useMemo(() => classnames(
		'relative w-full border border-neutral-200 dark:border-neutral-700 rounded-lg transition-colors',
		readOnly && 'border-0',
		(type === 'material' || type === 'large') && 'border-0'
	), [readOnly, type]);

	// Editor's container classnames
	const editableClassname = useMemo(() => classnames(
		'py-3 px-4 space-y-3 caret-neutral-800 dark:caret-white break-words',
		readOnly && 'py-0 px-0',
		type === 'material' && 'px-0 min-h-[200px]',
		type === 'large' && 'px-0 min-h-[1rem]'
	), [readOnly, type]);

	// Add spellcheck handlers to editor
	useEffect(() => {
		// Do not add undefined handlers
		if (!spellcheck) return;
		editor.current.words = spellcheck?.words;
		editor.current.onWordAdd = spellcheck?.onWordAdd;
		editor.current.onWordRemove = spellcheck?.onWordRemove;
		editor.current.onWordChange = spellcheck?.onWordChange;
		editor.current.spellcheckMode = spellcheck?.mode;
	}, [spellcheck]);

	// Add quiz handlers to editor
	useEffect(() => {
		// Do not add undefined handlers
		if (!quiz) return;
		editor.current.quizMode = quiz.mode;
	}, [quiz]);

	return (
		<div className="w-full max-w-full flex flex-col">
			{/* Show label only if passed */}
			<Show if={label}>
				<label
					className="mb-1 text-sm font-bold text-neutral-800 dark:text-white transition-colors"
				>
					{label}
				</label>
			</Show>

			<EditorContext.Provider value={{
				config: {
					path,
					folder,
				},
				quiz: {
					mode: quizContext.mode
				},
				onUpdateQuizMode
			}}>
				<Slate
					editor={editor.current}
					value={instance}
					onChange={handleChange}
				>
					<div className={editorClassname}>
						{/* Do not render on readOnly mode */}
						{!readOnly &&
						<Toolbar
							type={type}
							path={path}
							folder={folder}
						/>
						}

						{/* Editable's container */}
						<Editable
							spellCheck="false"
							readOnly={readOnly}
							renderLeaf={renderLeaf}
							renderElement={renderElement}
							className={editableClassname}
							onKeyDown={handleKeyDown}
							onPaste={handlePaste}
						/>
					</div>
				</Slate>
			</EditorContext.Provider>

			{/* Render error only if it has been passed */}
			{error && <div className="form__error">{error}</div>}
		</div>
	);
};

export default Editor;
